import { PROJECT_MEDIA_TYPES } from 'consts'
import { useEffect, useMemo, useState } from 'react'
import { videoThumbsService } from 'services/video-thumbs'
import { IMediaItem, MediaStatus } from 'types/media'

const videoTypes = [PROJECT_MEDIA_TYPES.video, PROJECT_MEDIA_TYPES.aiVideo]

/** All cached whole-second thumbs for the source file (same filmstrip width as main: originalDuration × pps, panned by trimStart). */
const mapToSortedUrlsForFullFilmstrip = (
  map: Map<number, string>,
  originalDuration: number | undefined
): string[] => {
  const end =
    originalDuration !== undefined && Number.isFinite(originalDuration)
      ? Math.ceil(originalDuration)
      : Number.POSITIVE_INFINITY
  return Array.from(map.entries())
    .filter(([ts]) => ts >= 0 && ts < end)
    .sort(([a], [b]) => a - b)
    .map(([, url]) => url)
}

export const useMediaItemFrames = (mediaItem: IMediaItem) => {
  const { assetId, meta, type } = mediaItem
  const isVideo = videoTypes.includes(type)
  const isReady = meta.status === MediaStatus.OK
  const originalDuration = meta.originalDuration

  const path = useMemo(
    () => meta.previewPath || meta.path || '',
    [meta.previewPath, meta.path]
  )

  const [frames, setFrames] = useState<string[]>(() => {
    if (!isVideo) return []
    return mapToSortedUrlsForFullFilmstrip(
      videoThumbsService.getCachedUrls(assetId),
      originalDuration
    )
  })

  useEffect(() => {
    if (!isVideo) {
      if (path) {
        setFrames([path])
      }
      return
    }

    if (!isReady) return

    return videoThumbsService.subscribe(assetId, map => {
      const entries = Array.from(map.entries())
        .filter(([timestamp]) => {
          const end =
            originalDuration !== undefined && Number.isFinite(originalDuration)
              ? Math.ceil(originalDuration)
              : Number.POSITIVE_INFINITY
          return timestamp >= 0 && timestamp < end
        })
        .sort(([a], [b]) => a - b)
      setFrames(entries.map(([, url]) => url))
    })
  }, [assetId, isVideo, isReady, path, originalDuration])

  return { frames, setFrames }
}
